/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaChevronRight, FaFilter } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getSearch } from '../../services/item';
import { setDrawerOpen } from '../../store/global';
import { linkWithMJparam } from '../../utils/roles';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import { InputTransparent } from '../form/input.Transparent';
import { isTabCampaignAccessible } from '../../utils/item';
import { Checkbox } from '../form/checkbox';
import { useSyncedCampaignFilter } from '../../hooks/useSyncedCampaignFilter';

export const SearchInput = props => {
  const {
    itemId,
    searchIn,
    className,
    callback,
    enableCampaignFilter = false,
    showNavigateIcon = true,
  } = props;

  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const listCampaignsId = useSelector(state => state.global.listCampaignsId);
  const [fallbackCampaignIds, setFallbackCampaignIds] = useState([]);
  const mjQuery = isMjForItem(item, items) ? '?' + linkWithMJparam() : '';
  const [searchedItemsList, setSearchedItemsList] = useState([]);
  const [searchedItem, setSearchedItem] = useState('');
  const [showCampaignFilter, setShowCampaignFilter] = useState(false);
  const universeId = item?.type === 'universe' ? item?._id : item?.universe;
  const effectiveCampaignIds = useMemo(() => {
    if (Array.isArray(listCampaignsId) && listCampaignsId.length > 0) {
      return listCampaignsId;
    }
    return fallbackCampaignIds;
  }, [listCampaignsId, fallbackCampaignIds]);

  const [selectedCampaignIds, setSelectedCampaignIds] =
    useSyncedCampaignFilter(effectiveCampaignIds, 'item-search-campaigns');
  const dispatch = useDispatch();

  useEffect(() => {
    if (!enableCampaignFilter) {
      return;
    }
    if (!universeId) {
      return;
    }
    if (Array.isArray(listCampaignsId) && listCampaignsId.length > 0) {
      return;
    }
    let cancelled = false;
    const loadCampaigns = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/items/allcampaigns/${universeId}`,
        );
        if (cancelled) {
          return;
        }
        const ids = Array.isArray(res?.data)
          ? res.data
              .map(campaign => campaign?._id)
              .filter(Boolean)
          : [];
        setFallbackCampaignIds(ids);
      } catch (error) {
        if (!cancelled) {
          setFallbackCampaignIds([]);
        }
      }
    };
    loadCampaigns();
    return () => {
      cancelled = true;
    };
  }, [enableCampaignFilter, universeId, listCampaignsId]);

  const campaignOptions = useMemo(() => {
    if (!Array.isArray(effectiveCampaignIds)) {
      return [];
    }
    return effectiveCampaignIds.filter(Boolean).map(id => ({
      id,
      label: items?.[id]?.label || 'Dossier',
    }));
  }, [effectiveCampaignIds, items]);

  const search = async value => {
    try {
      const campaignFilterReady =
        Array.isArray(effectiveCampaignIds) && effectiveCampaignIds.length > 0;
      const campaignsForSearch = enableCampaignFilter
        ? campaignFilterReady
          ? selectedCampaignIds
          : undefined
        : effectiveCampaignIds;
      if (
        enableCampaignFilter &&
        campaignFilterReady &&
        Array.isArray(campaignsForSearch) &&
        campaignsForSearch.length === 0
      ) {
        setSearchedItemsList([]);
        return;
      }
      const res = await getSearch(value, item, searchIn, campaignsForSearch);
      if (Array.isArray(res)) {
        //order res alphabetically
        res.sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }
          return 0;
        });

        const filtered = res.filter(
          searchItem =>
            searchItem?.type !== 'tab' ||
            isTabCampaignAccessible(searchItem, items, effectiveCampaignIds),
        );
        setSearchedItemsList(filtered);
      } else {
        setSearchedItemsList([]);
      }
    } catch (error) {
      setSearchedItemsList([]);
    }
  };

  useEffect(() => {
    if (searchedItem?._id) {
      callback
        ? callback(searchedItem)
        : dispatch(setDrawerOpen(searchedItem._id));
    }
  }, [searchedItem]);

  return (
    <div className={`w-full min-w-0 ${className || ''}`}>
      <div className='w-full min-w-0 flex items-center gap-05'>
        <InputTransparent
          resetValue={searchedItem}
          forcedValue={searchedItem?.label}
          placeholder='chercher un item'
          onChange={e => search(e.target.value)}
          onBlur={() => null}
          className='flex-1 min-w-0 m-0 px-1 py-05'
        />
        {enableCampaignFilter && campaignOptions.length > 0 && (
          <button
            type='button'
            className={`mr-1 transition hover:opacity-70 ${
              showCampaignFilter ||
              selectedCampaignIds.length !== campaignOptions.length
                ? 'text-secondary'
                : 'text-contrast text-opacity-70'
            }`}
            title='Filtrer les dossiers'
            onClick={() => setShowCampaignFilter(prev => !prev)}
          >
            <FaFilter />
          </button>
        )}
      </div>
      {enableCampaignFilter &&
        campaignOptions.length > 0 &&
        showCampaignFilter && (
          <div className='-ml-05 p-05 mt-03 bg-main-d bg-opacity-50 max-h-10 overflow-auto'>
            {campaignOptions.map(option => {
              const checked = selectedCampaignIds.includes(option.id);
              return (
                <div
                  key={option.id}
                  className='py-02'
                >
                  <Checkbox
                    checked={checked}
                    size='sm'
                    label={option.label}
                    labelClassName='text-xs'
                    onChange={() =>
                      setSelectedCampaignIds(prev =>
                        prev.includes(option.id)
                          ? prev.filter(id => id !== option.id)
                          : [...prev, option.id],
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
      <div className='w-full'>
        {searchedItemsList.map(searchItem => (
          <div
            key={searchItem._id}
            className='flex gap-1 items-center w-full'
          >
            <div
              className='w-full flex-1 cursor-pointer flex items-center'
              onClick={() => {
                setSearchedItem(searchItem);
                setSearchedItemsList([]);
              }}
            >
              <div className='flex-1 ml-05 text-contrast'>{searchItem.label}</div>
              {showNavigateIcon &&
                (isMjForItem(searchItem, items) ||
                  canUseMjForItem(searchItem, items)) && (
                <Link
                  to={`../../item/${searchItem._id}${mjQuery}`}
                  className='flex justify-end pr-05 cursor-pointer transition hover:opacity-50'
                >
                  <FaChevronRight />
                </Link>
                )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
